import { useEffect, useState } from "react";
import { formatBoxSizeMm, formatSizeMm, parseBoxSizeMm, parseSizeMm } from "@apv-erp/status";
import { cn } from "@/lib/utils";

/**
 * Ô kích thước chuẩn (04/11/2026): 2 ô số Rộng × Dài + chọn mm / cm; luôn lưu "R x D mm" (VD "650x860 mm").
 * Chuỗi cũ gõ tay ("65x86", "79x109 cm") được đọc và hiện thành số; lưu lại thì về dạng chuẩn.
 */
export function SizeInput({
  value,
  onChange,
  onBlur,
  disabled,
  className,
  size = "md",
  testId,
}: {
  value?: string | null;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
  size?: "sm" | "md";
  testId?: string;
}) {
  const [unit, setUnit] = useState<"mm" | "cm">("mm");
  const [a, setA] = useState("");
  const [b, setB] = useState("");

  // Đồng bộ từ giá trị ngoài (mở bản ghi cũ / đổi bản ghi)
  useEffect(() => {
    const parsed = parseSizeMm(value);
    const factor = unit === "cm" ? 10 : 1;
    const current = parseSizeMm(formatSizeMm(Number(a) * factor, Number(b) * factor));
    if (parsed && current && parsed.width === current.width && parsed.length === current.length) return;
    if (!value) {
      if (a || b) {
        setA("");
        setB("");
      }
      return;
    }
    if (parsed) {
      setA(String(parsed.width / factor));
      setB(String(parsed.length / factor));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (na: string, nb: string, u: "mm" | "cm") => {
    const f = u === "cm" ? 10 : 1;
    const wa = parseFloat(na.replace(",", "."));
    const wb = parseFloat(nb.replace(",", "."));
    if (!na && !nb) return onChange("");
    if (wa > 0 && wb > 0) onChange(formatSizeMm(wa * f, wb * f));
  };

  const h = size === "sm" ? "h-7 text-xs" : "h-9 text-sm";
  const box = cn(
    "w-full min-w-0 rounded-md border border-input bg-background px-2 text-right tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
    h,
  );
  return (
    <div className={cn("flex items-center gap-1", className)} data-testid={testId}>
      <input
        inputMode="decimal"
        aria-label="Rộng"
        placeholder="Rộng"
        className={box}
        value={a}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value.replace(/[^\d.,]/g, "");
          setA(v);
          emit(v, b, unit);
        }}
        onBlur={onBlur}
      />
      <span className="shrink-0 text-xs text-slate-500">×</span>
      <input
        inputMode="decimal"
        aria-label="Dài"
        placeholder="Dài"
        className={box}
        value={b}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value.replace(/[^\d.,]/g, "");
          setB(v);
          emit(a, v, unit);
        }}
        onBlur={onBlur}
      />
      <select
        aria-label="Đơn vị kích thước"
        className={cn("shrink-0 rounded-md border border-input bg-background px-1 text-xs", h)}
        value={unit}
        disabled={disabled}
        onChange={(e) => {
          const nu = e.target.value as "mm" | "cm";
          // Đổi đơn vị hiển thị, giữ nguyên kích thước thật
          const f = nu === unit ? 1 : nu === "cm" ? 0.1 : 10;
          const na = a ? String(Math.round(parseFloat(a) * f * 100) / 100) : "";
          const nb = b ? String(Math.round(parseFloat(b) * f * 100) / 100) : "";
          setUnit(nu);
          setA(na);
          setB(nb);
        }}
      >
        <option value="mm">mm</option>
        <option value="cm">cm</option>
      </select>
    </div>
  );
}

/**
 * Ô kích thước thùng (05/11/2026, FB-GDACEX): 3 ô số Dài × Rộng × Cao (mm) có sẵn dấu ×; lưu "D x R x C mm".
 * Chuỗi cũ gõ tay ("600x400x300", "60*40*30 cm") được đọc lại thành 3 số.
 */
export function BoxSizeInput({
  value,
  onChange,
  disabled,
  className,
  testId,
}: {
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  testId?: string;
}) {
  const [dims, setDims] = useState<[string, string, string]>(["", "", ""]);

  useEffect(() => {
    const parsed = parseBoxSizeMm(value);
    const current = parseBoxSizeMm(formatBoxSizeMm(Number(dims[0]), Number(dims[1]), Number(dims[2])));
    if (parsed && current && parsed.length === current.length && parsed.width === current.width && parsed.height === current.height) return;
    if (parsed) setDims([String(parsed.length), String(parsed.width), String(parsed.height)]);
    else if (!value && dims.some(Boolean)) setDims(["", "", ""]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const update = (i: number, raw: string) => {
    const v = raw.replace(/[^\d.,]/g, "");
    const next = [...dims] as [string, string, string];
    next[i] = v;
    setDims(next);
    const [l, w, h] = next.map((x) => parseFloat(x.replace(",", ".")));
    if (next.every((x) => !x)) onChange("");
    else if (l > 0 && w > 0 && h > 0) onChange(formatBoxSizeMm(l, w, h));
  };

  const box =
    "h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-right text-sm tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";
  const labels = ["Dài", "Rộng", "Cao"];
  return (
    <div className={cn("flex items-center gap-1", className)} data-testid={testId}>
      {labels.map((label, i) => (
        <div key={label} className="contents">
          {i > 0 && <span className="shrink-0 text-xs text-slate-500">×</span>}
          <input
            inputMode="decimal"
            aria-label={label}
            placeholder={label}
            className={box}
            value={dims[i]}
            disabled={disabled}
            onChange={(e) => update(i, e.target.value)}
          />
        </div>
      ))}
      <span className="shrink-0 text-xs text-slate-500">mm</span>
    </div>
  );
}
